/**
 * 환원보류 기간임박 처리창 — $j.F.Admin.Order.WhanHold
 *
 * 환원보류를 걸어둔 채 1년 마지노선이 다가온 오더 한 건을 **그 자리에서** 끝낸다.
 * 접수목록에는 이 일을 여는 창이 없어서(오더 한 줄을 찾아 오더핸들까지 들어가야 했다)
 * 알림 모아보기가 이 창을 연다.
 *
 *   $j.F.Admin.Order.WhanHold.config({ onChanged: reloadTable });   // 화면 1회
 *   $j.F.Admin.Order.WhanHold.open(ordernum);                       // 열기
 *
 * 끝내는 길은 둘이다 — 원장은 둘 중 무엇이든 소스가 바뀌면 따라 닫힌다(WhanwonProvider).
 *   보류 해제   whan_status → NULL. 다음 환원배치(매월1일)가 환원한다
 *   수금완료    받을 돈을 받았다. 환원 대상에서 빠진다
 * 보류를 그대로 두는 것은 처리가 아니다 — 마지노선이 지나도 목록에 남는다.
 *
 * 값은 목록과 같은 것을 읽는다(`/adm/ajax/tasks/detail` → `WhanwonProvider::details`).
 * 열기 전에 원장을 본다($j.F.Admin.TaskGuard) — 다른 처리창과 같은 문구·같은 규칙이다.
 */
export default class WhanHold {
    /** 원장에서 이 종류를 부르는 이름 */
    static TASK_TYPE = 'whanwon';

    /** 수금 방법 — 오더핸들의 수금완료 창과 같은 셋 */

    constructor() {
        this.onChanged = null;
        this.handle    = null;
        this.row       = null;
    }

    /** @param {object} opts onChanged: 처리 뒤 목록을 다시 받게 하는 콜백 */
    config(opts = {}) {
        if (opts.onChanged) this.onChanged = opts.onChanged;
        this._bind();
        return this;
    }

    /** 오더 한 건을 연다. 인자는 오더번호 하나 — 원장이 아는 것이 그것뿐이다 */
    async open(ordernum) {
        if (!await $j.F.Admin.TaskGuard.check(WhanHold.TASK_TYPE, ordernum,
                { onClaimed: () => this._changed() })) return;

        let r;
        try {
            r = await $.post('/adm/ajax/tasks/detail', { type: WhanHold.TASK_TYPE, ref: ordernum });
        } catch (e) {
            $j.Ui.Toast.show('오더를 불러오지 못했습니다.', { type: 'warn' });
            return;
        }
        if (!r || !r.result) {
            $j.Ui.Toast.show((r && r.message) || '없는 오더입니다.', { type: 'warn' });
            return;
        }

        this._bind();
        this._injectStyle();

        this.row = r.row;
        this.handle = $j.Ui.Modal.open({
            id: 'whan-hold', width: '520px', variant: 'admin',
            title: `환원보류 — 오더 ${r.row.ordernum}`,
            html: this._body(r.row),
        });

        $j.F.Admin.TaskBadge.attach(this.handle.el, WhanHold.TASK_TYPE, ordernum,
            { onChanged: () => this._changed() });
    }

    _body(d) {
        const won  = (n) => Number(n || 0).toLocaleString() + '원';
        const who  = (no, name) => no ? $j.F.Admin.Member.Pop.cell(no, name, { stop: true }) : '<span class="text-muted">-</span>';
        const left = Number(d.d_left);
        const dday = left < 0
            ? `<span class="wh-late">${-left}일 지남</span>`
            : `<span class="${left <= 15 ? 'wh-late' : ''}">D-${left}</span>`;

        const rows = [
            ['오더', `<a href="javascript:;" data-wh-order="${d.ordernum}">${d.ordernum}</a>`
                     + ` <span class="text-muted">· ${this._esc(d.car_type)} · ${this._esc(d.status_label)}</span>`],
            ['작업일', this._esc(d.work_day)],
            ['마지노선', `${this._esc(d.deadline)} ${dday}`],
            ['장비대금', won(d.car_price) + (d.fee_rate ? ` <span class="text-muted">· 수수료 ${d.fee_rate}%</span>` : '')],
            ['발주자', who(d.owner_no, d.owner_name)],
            ['수주자', who(d.worker_no, d.worker_name)],
            ['현장', this._esc(d.site_address) || '<span class="text-muted">-</span>'],
            ['보류 사유', d.hold_memo
                ? `${this._esc(d.hold_memo)} <span class="text-muted">· ${this._esc((d.hold_at || '').slice(0, 10))}</span>`
                : '<span class="text-muted">기록 없음</span>'],
        ].map(([k, v]) => `<div class="wh-row"><span>${k}</span><b>${v}</b></div>`).join('');

        // 이미 끝난 건(보류가 풀렸거나 수금됐거나) — 읽기만
        const open = Number(d.whan_status) === 1 && !Number(d.is_paid);
        if (!open) {
            const why = Number(d.is_paid) ? '이미 수금완료된 오더입니다.'
                : d.whan_status === null ? '보류가 해제된 오더입니다. 다음 환원배치가 처리합니다.'
                : '이미 환원된 오더입니다.';
            return `<div class="wh-case">${rows}<div class="wh-note">${why}</div></div>`;
        }

        return `<div class="wh-case">${rows}
            <div class="wh-sec">
                <div class="wh-sec__h">돈을 받았으면 — 수금완료</div>
                <div class="wh-actions">
                    <button type="button" class="btn btn-primary" data-wh-act="pay">수금완료</button>
                </div>
            </div>
            <div class="wh-sec">
                <div class="wh-sec__h">받을 길이 없으면 — 보류 해제</div>
                <div class="wh-desc">다음 환원배치(매월 1일)에서 환원됩니다.</div>
                <div class="wh-actions">
                    <button type="button" class="btn btn-danger" data-wh-act="release">보류 해제</button>
                </div>
            </div>
        </div>`;
    }

    /** 모달은 body 밑에 붙으므로 document 위임. 한 번만 건다 */
    _bind() {
        if (this._bound) return;
        this._bound = true;

        $(document).on('click.whanhold', '#whan-hold [data-wh-act]', (e) => {
            const act = $(e.currentTarget).data('wh-act');
            if (act === 'pay') this._pay();
            if (act === 'release') this._release();
        });
        $(document).on('click.whanhold', '#whan-hold [data-wh-order]', (e) => {
            $j.F.Admin.Util.openOrderHandle(Number($(e.currentTarget).data('wh-order')));
        });
    }

    async _release() {
        const d = this.row;
        if (!d) return;

        const ok = await $j.Ui.Dialog.confirm('환원보류를 해제할까요?', {
            maxWidth: 380, desc: '다음 환원배치(매월 1일)부터 다시 환원 대상에 들어갑니다.', confirm: '해제',
        });
        if (!ok) return;

        const r = await $.post(`/adm/ajax/order/${d.ordernum}/whanwon/defer-release`);
        if (!r || !Number(r.result)) {
            $j.Ui.Toast.show('이미 보류가 풀린 오더입니다.', { type: 'warn' });
            return;
        }
        this._done('환원보류를 해제했습니다.');
    }

    /**
     * 수금완료 — 오더핸들의 수금완료와 같은 엔드포인트. 작업완료 전이면 서버가 작업완료부터 찍는다.
     * 카드결제 결과 확인 중인 오더는 서버가 한 번 묻게 한다(card_pending_ok).
     */
    async _pay(extra = {}) {
        const d = this.row;
        if (!d) return;

        const r = await $.post(`/adm/ajax/order/${d.ordernum}/pay-done`, extra);

        if (r && r.confirm) {
            if (await $j.Ui.Dialog.confirm(r.message, { maxWidth: 420, confirm: '수금완료' })) {
                this._pay({ card_pending_ok: 1 });
            }
            return;
        }
        if (r && r.result === false) {
            $j.Ui.Toast.show(r.message || '처리하지 못했습니다.', { type: 'warn' });
            return;
        }
        this._done('수금완료 했습니다.');
    }

    _done(msg) {
        $j.Ui.Toast.show(msg, { type: 'success' });
        if (this.handle) this.handle.close();
        this._changed();
    }

    _changed() {
        if (this.onChanged) this.onChanged();
    }

    /** 창 안쪽 모양. 모달은 body 밑에 붙으므로 페이지 CSS 에 둘 수 없다 — 1회만 주입한다 */
    _injectStyle() {
        if (document.getElementById('whan-hold-style')) return;
        const el = document.createElement('style');
        el.id = 'whan-hold-style';
        el.textContent = `
.wh-case .wh-row{display:flex;justify-content:space-between;gap:14px;
  padding:6px 2px;border-bottom:1px solid var(--adm-border);font-size:13px}
.wh-case .wh-row span{color:var(--adm-text-muted);flex:none}
.wh-case .wh-row b{font-weight:600;color:var(--adm-text-heading);text-align:right;word-break:break-all}
.wh-case .wh-late{color:var(--adm-ink-red);font-weight:700}
.wh-case .wh-note{margin-top:14px;font-size:12.5px;color:var(--adm-text-faint)}
.wh-case .wh-sec{margin-top:14px;padding:10px 12px;border:1px solid var(--adm-border);
  border-radius:var(--adm-radius-md);background:var(--adm-surface-soft)}
.wh-case .wh-sec__h{font-size:12.5px;font-weight:700;color:var(--adm-text-heading);margin-bottom:6px}
.wh-case .wh-desc{font-size:12px;color:var(--adm-text-muted)}
.wh-case .wh-actions{display:flex;justify-content:flex-end;margin-top:8px}`;
        document.head.appendChild(el);
    }

    _esc(s) {
        return String(s == null ? '' : s).replace(/[&<>"']/g, (c) =>
            ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    }
}
